<script module>
	export { ItemBadge, ItemDescription, ItemNativeName, ItemQuickFacts, ItemTags, ItemTitle, ItemViewPrimary };
</script>

<script lang="ts">
	import ImageGallery from '$lib/components/ui/ImageGallery.svelte';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import type { PrimaryLink } from '$lib/features/_other/primaryLink.type';
	import { CITY_FEATURE_PAGE_PER_ITEM_TYPES } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { ImageDetails } from '$lib/features/image/imageDetails.type';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import type { Component, Snippet } from 'svelte';
	import IconArrowRight from '~icons/mdi/arrow-right';

	const {
		englishName,
		images,
		eagerLoadImages,
		TextContentSnippet
	}: {
		englishName: string;
		images: ImageDetails[];
		eagerLoadImages: boolean;
		TextContentSnippet: Snippet;
	} = $props();
</script>

<div class="grid w-full grid-cols-1 items-start gap-8 lg:grid-cols-12 lg:gap-12">
	<div class={images.length > 0 ? 'flex flex-col gap-6 lg:col-span-7' : 'flex flex-col gap-6 lg:col-span-12'}>
		{@render TextContentSnippet()}
	</div>

	{#if images.length > 0}
		<div class="h-fit lg:sticky lg:top-28 lg:col-span-5">
			<ImageGallery
				classes="w-full h-auto"
				internalClasses="!h-auto max-h-[60vh] lg:max-h-[45vh]"
				{images}
				ariaLabel={`View "${englishName}" gallery`}
				eagerLoad={eagerLoadImages}
			/>
		</div>
	{/if}
</div>

{#snippet ItemBadge({ Icon, text }: { Icon?: Component; text: string })}
	<span class="badge badge-md font-semibold tracking-widest shadow-sm badge-secondary">
		{#if Icon}
			<Icon />
		{/if}
		{text}
	</span>
{/snippet}

{#snippet ItemTitle({ headingTag = 'h2', englishName }: { headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5'; englishName: string })}
	<svelte:element this={headingTag} class="text-2xl text-base-content md:text-3xl lg:text-4xl">
		{englishName}
	</svelte:element>
{/snippet}

{#snippet ItemNativeName({ nativeName, language }: { nativeName: string | null; language: Language })}
	{#if nativeName}
		<div class="mt-1 border-l-2 border-primary/10 pl-4">
			<NativeText
				classes="text-sm font-medium tracking-widest text-base-content/70"
				languageCode={language.code}
				isLanguageRTL={language.rtl}
				text={nativeName.toUpperCase()}
			/>
		</div>
	{/if}
{/snippet}

{#snippet ItemDescription({ text }: { text: string })}
	<p class="text-base leading-relaxed text-base-content/80 md:text-lg">
		{text}
	</p>
{/snippet}

{#snippet ItemViewPrimary({ text, primary, location }: { text?: string; primary: PrimaryLink | null; location: PlaceLocation })}
	{#if primary}
		{const cityFeatureNav: NavLink = UrlUtil.toCityFeature({ location, type: primary.type, featureSlug: primary.slug })}
		<a
			class="group btn gap-2 rounded-full font-mono shadow-sm btn-primary btn-sm md:btn-md"
			href={`/${cityFeatureNav.href}`}
			data-sveltekit-preload-data={cityFeatureNav.preloadData}
			data-sveltekit-preload-code={cityFeatureNav.preloadCode}
		>
			<span>
				{#if text}
					{text}
				{:else if CITY_FEATURE_PAGE_PER_ITEM_TYPES.includes(primary.type)}
					view detailed guide
				{:else}
					view more
				{/if}
			</span>
			<IconArrowRight class="text-base transition-transform group-hover:translate-x-1" />
		</a>
	{/if}
{/snippet}

{#snippet ItemTags({ heading, tags }: { heading: { text: string; Icon: Component }; tags: string[] })}
	{#if tags.length > 0}
		<div class="flex flex-col gap-3 rounded-box border border-base-content/10 bg-base-200 p-5">
			<span class="flex items-center gap-1.5 text-xs font-bold tracking-widest text-base-content/70">
				<heading.Icon class="text-base text-primary" />
				{heading.text}
			</span>
			<div class="flex flex-wrap gap-2">
				{#each tags as tag (tag)}
					<span class="badge badge-sm font-semibold capitalize shadow-sm badge-accent">
						{tag}
					</span>
				{/each}
			</div>
		</div>
	{/if}
{/snippet}

{#snippet ItemQuickFacts({ facts }: { facts: { text: string; title: string; Icon: Component }[] })}
	<div class="flex flex-wrap items-center gap-2">
		{#each facts as fact (fact.text)}
			<span class="badge badge-soft px-3 py-3 text-xs font-bold tracking-wider badge-info" title={fact.title}>
				<fact.Icon class="mr-1 text-sm" />
				<span>{fact.text.toLowerCase().toUpperCase()}</span>
			</span>
		{/each}
	</div>
{/snippet}
